import cn from 'classnames'
import { motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React, { useState } from 'react'

import {
   ArrowDown02Icon,
   ChevronDownIcon
} from '@flowwai-frontend/design-system/icons'
import {
   ExpandAndCollapse,
   LocationValue
} from '@flowwai-frontend/shared/common-components'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../types'
import { getCoordinatesForGeoShape } from '../../utils/GeoFieldsUtil'
import CoordinatesList from '../CoordinatesList/CoordinatesList'
import PlacesSearchTool from '../MapsToolbar/components/PlacesSearchTool'

import * as Styles from './styles'
import './styles.css'

interface Props {
   isReadOnly: boolean
   geoShape: LocationValue
   validateAndUpdateGeoShape: (geoShape: LocationValue) => void
   onAddCoordinates: (clickedCoordinates: MapCoordinates) => void
   onMoveMarker: (coordinates: MapCoordinates, index: number) => void
   onDeleteCoordinates: (index: number) => void
   onSearchLocation: (coordinates: MapCoordinates) => void
}

const CoordinatesListWithSearch = (props: Props): React.ReactElement => {
   const {
      geoShape,
      isReadOnly,
      validateAndUpdateGeoShape,
      onSearchLocation
   } = props

   const [isExpanded, setIsExpanded] = useState(false)

   const toggleExpansion = () => setIsExpanded(prevState => !prevState)

   const {
      mapsT,
      serviceProvidersConfig: { search: searchServiceProviderConfig }
   } = useMapsUtilityContext()

   const isMobileDevice = isMobile()

   const coordinates = getCoordinatesForGeoShape(geoShape)

   const renderMobileHeaderToggle = (): React.ReactElement => (
      <div
         className={cn(
            Styles.mobileHeaderToggleContainer,
            isMobileDevice ? 'text-sm-medium' : 'text-md-medium',
            isExpanded ? 'py-2' : 'pt-2'
         )}
         onClick={toggleExpansion}
      >
         <p>{mapsT('locationCoordinates')}</p>

         {isMobileDevice ? (
            <div
               className={cn(
                  'transition-transform',
                  isExpanded ? 'rotate-180' : ''
               )}
            >
               <ChevronDownIcon width={20} height={20} />
            </div>
         ) : null}
      </div>
   )

   const renderDesktopHeaderToggle = (): React.ReactElement => (
      <div
         className={cn(Styles.desktopHeaderToggleContainer)}
         onClick={toggleExpansion}
      >
         <div
            className={cn(
               'transition-transform',
               isExpanded ? '' : '-rotate-90'
            )}
         >
            <ArrowDown02Icon width={20} height={20} />
         </div>
         <p>
            {mapsT(
               coordinates.length
                  ? 'mapCoordinatesWithCount'
                  : 'mapCoordinates',
               { count: coordinates.length }
            )}
         </p>
      </div>
   )

   const renderPlacesSearch = (): React.ReactElement => (
      <PlacesSearchTool onSearchLocation={onSearchLocation} alwaysExpanded />
   )

   const renderCoordinates = (): React.ReactElement => (
      <div className={'mt-3'}>
         <CoordinatesList
            isReadOnly={isReadOnly}
            geoShape={geoShape}
            validateAndUpdateGeoShape={validateAndUpdateGeoShape}
            onAddCoordinates={props.onAddCoordinates}
            onMoveMarker={props.onMoveMarker}
            onDeleteCoordinates={props.onDeleteCoordinates}
            listContainerClassName={Styles.coordinatesListContainer}
         />
      </div>
   )

   return (
      <motion.div
         className={cn(
            Styles.container,
            isMobileDevice
               ? 'w-[calc(100%-16px)] top-2 left-3'
               : 'w-[360px] top-6 left-6'
         )}
         {...Styles.animationProps}
      >
         {renderPlacesSearch()}

         {!coordinates.length && isReadOnly ? null : (
            <div className={cn(Styles.innerContainer)}>
               {isMobileDevice
                  ? renderMobileHeaderToggle()
                  : renderDesktopHeaderToggle()}

               <ExpandAndCollapse isOpened={isExpanded}>
                  {renderCoordinates()}
               </ExpandAndCollapse>
            </div>
         )}
      </motion.div>
   )
}

export default observer(CoordinatesListWithSearch)
